import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ODataParams, ODataResponse, odataParamsFactory } from '@tdw/core';
import { ENV_CONFIG, EnvironmentConfig } from 'src/environments/environment.model';
import { LookupValue, PigSigLookup } from '../models/lookup.model';
import { Observable, catchError, forkJoin, map, of, retry, throwError } from 'rxjs';
import { PigSigFlangeHeight, menuDataType, PigSigProduct, SpareParts, PigSigWallThickness, pigSigQueryParams } from '../models/pig-sig-selector.model';
import { Inventtxt } from '../models/fitting-query-params.model';
import { PigSigFilter, PigSigQueryResult } from '../store/fromPigSig.barrel';

@Injectable({
  providedIn: 'root'
})
export class PigSigService {

  private endpoint: { url: string; version: string; };

  constructor(
    private http: HttpClient,
    @Inject(ENV_CONFIG) private config: EnvironmentConfig) {
    this.endpoint = this.config.endpoints.baseApi;
    // const currentDate = new Date();
    // const q = odataParamsFactory({
    //     // filter: `(inactiveDate eq null OR inactiveDate lt ${currentDate.toISOString()}) AND showInSelector eq 1`,
    //     orderBy: 'SortOrder'
    //   });
    //   console.log(q);
  }

  getIndicators(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigIndicators${q.toQuery()}`);
  }
  getPlugs(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigPlugAssemblys${q.toQuery()}`);
  }
  getShafts(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigShaftOptions${q.toQuery()}`);
  }
  getOrings(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigOrings${q.toQuery()}`);
  }
  getConnections(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigConnectionTypes${q.toQuery()}`);
  }
  getFlangeMatls(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigFlangeMatls${q.toQuery()}`);
  }
  getFlangeHeights(q: ODataParams): Observable<ODataResponse<PigSigFlangeHeight[]>> {
    return this.http.get<ODataResponse<PigSigFlangeHeight[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigFlgHeights${q.toQuery()}`);
  }

  getSpareParts(q: ODataParams): Observable<ODataResponse<SpareParts[]>> {
    return this.http.get<ODataResponse<SpareParts[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigSpareParts${q.toQuery()}`);
  }
  getDiameters(q: ODataParams): Observable<ODataResponse<PigSigLookup[]>> {
    return this.http.get<ODataResponse<PigSigLookup[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigDiameters${q.toQuery()}`);
  }
  getThicknesses(): Observable<ODataResponse<PigSigWallThickness[]>> {
    return this.http.get<ODataResponse<PigSigWallThickness[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigWallThickness`);
  }
  getLookups() {
    var currentDate = new Date();
    const q = `inactivedate eq null OR inactivedate gt ${currentDate}`;
    const oq = odataParamsFactory({ filter: q, orderBy: 'SortOrder' })
    return forkJoin([
      this.getIndicators(oq),
      this.getPlugs(oq),
      this.getShafts(oq),
      this.getOrings(oq),
      this.getConnections(oq)
    ]).pipe(map(([indicators, Plugs, Shafts, Orings, Connections]) => ({
      Indicators: indicators, Plugs, Shafts, Orings, Connections
    })))
  }

  getFilterResult(q: PigSigFilter): Observable<PigSigQueryResult> {
    return this.http.post<PigSigQueryResult>(`${this.endpoint?.url}api/${this.endpoint?.version}/PigSigs`, q);
  }

  setCommercialDescription(pigSigProducts: PigSigProduct[], Inventtxt: Inventtxt[]) {
    pigSigProducts.forEach((psp) => {
      var txt = Inventtxt.find((i) => i.itemid = psp.partNumber)?.txt;
      if (txt && txt?.length > 0) {
        psp.description = txt;
      }
    })
    return pigSigProducts;
  }

  getPigSigProducts2(partno: string): Observable<PigSigProduct[]> {
    return forkJoin([
      this.getPigSigProducts(partno),
      this.getCommercialDescriptions(partno)
    ]).pipe(map(([pigSigProducts, Inventtxts]) => this.setCommercialDescription(pigSigProducts, Inventtxts)))
  }

  getPigSigProducts(partno: string): Observable<PigSigProduct[]> {
    const currentDate = new Date().toISOString();
    const q = odataParamsFactory({ filter: `contains(partNumber, \'${partno}\') AND inactivedate eq null OR inactivedate gt ${currentDate}`, orderBy: 'partNumber' })
    return this.http.get<ODataResponse<PigSigProduct[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigProducts${q.toQuery()}`)
      .pipe(map(m => m.value || []));
  }

  getFlangePartNumber(pipeSizePN: string, flgHeight: number): Observable<PigSigFlangeHeight[]> {
    const currentDate = new Date().toISOString();
    const q = odataParamsFactory({ filter: `PipeSizePNVal eq \'${pipeSizePN}\' AND MinHeight le ${flgHeight} AND MaxHeight ge ${flgHeight} AND inactivedate eq null OR inactivedate gt ${currentDate}` });
    return this.http.get<ODataResponse<PigSigFlangeHeight[]>>(`${this.endpoint?.url}odata/${this.endpoint?.version}/PigSigFlgHeights${q.toQuery()}`)
      .pipe(map(m => m.value || []));
  }

  getFlatterFilesDrawingByPartNumber(partNumber: string): Observable<Blob> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/flatterfiles/${partNumber}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(
        map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' }))
      )
  }

  private getCommercialDescriptions(itemId: string): Observable<Inventtxt[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ filter: `contains(ItemId,'${itemId}')` })
    return this.http.get<ODataResponse<Inventtxt[]>>(`${endpoint?.url}odata/${endpoint?.version}/Inventtxts${q.toQuery()}`)
      .pipe(map(m => m.value || []));
  }
}
